import React, { useState, useEffect } from 'react';
import useWebSocket from '../hooks/useWebSocket';
import gameService from '../services/gameService';
import useTranslation from '../i18n/useTranslation';
import '../styles/pixel-art.css';
import '../styles/ai-player.css';

const Room = ({ roomCode, inviteLink, onGameStarted, onLeave }) => {
  const [players, setPlayers] = useState([]);
  const [isCreator, setIsCreator] = useState(false);
  const [gameStarting, setGameStarting] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const { isConnected, reconnecting, reconnectAttempt, startGame, leaveRoom, kickPlayer, addAIPlayer, removeAIPlayer, onMessage, MSG_TYPES } = useWebSocket();
  const { t } = useTranslation();

  useEffect(() => {
    // Initialize from gameService state on mount
    const currentState = gameService.getState();
    if (currentState) {
      if (currentState.players) {
        setPlayers(currentState.players);
      }
      if (currentState.isCreator !== undefined) {
        setIsCreator(currentState.isCreator);
      }
    }

    // Subscribe to gameService updates
    const unsubscribeGameService = gameService.subscribe((state) => {
      if (state && state.players) {
        setPlayers(state.players);
      }
      if (state && state.isCreator !== undefined) {
        setIsCreator(state.isCreator);
      }
    });

    // Listen for player joined
    const unsubscribeJoined = onMessage(MSG_TYPES.PLAYER_JOINED, (message) => {
      if (message.data) {
        setPlayers(message.data.players || []);
        setIsCreator(message.data.isCreator || false);
      }
    });

    // Listen for player left
    const unsubscribeLeft = onMessage(MSG_TYPES.PLAYER_LEFT, (message) => {
      if (message.data) {
        setPlayers(message.data.players || []);
      }
    });

    // Listen for game started
    const unsubscribeStarted = onMessage(MSG_TYPES.GAME_STARTED, () => {
      setGameStarting(true);
      setTimeout(() => {
        onGameStarted();
      }, 500);
    });

    // Listen for game state (for rejoining in-progress games)
    const unsubscribeGameState = onMessage(MSG_TYPES.GAME_STATE, () => {
      // Game is already in progress, transition immediately
      console.log('Received GAME_STATE, transitioning to game board');
      setGameStarting(true);
      setTimeout(() => {
        onGameStarted();
      }, 100);
    });

    // Listen for being kicked
    const unsubscribeKicked = onMessage(MSG_TYPES.PLAYER_KICKED, () => {
      alert(t.room.youWereKicked);
      gameService.reset();
      onLeave();
    });

    // Listen for AI player added
    const unsubscribeAIAdded = onMessage(MSG_TYPES.AI_PLAYER_ADDED, (message) => {
      if (message.data && message.data.players) {
        setPlayers(message.data.players);
      }
    });

    // Listen for AI player removed
    const unsubscribeAIRemoved = onMessage(MSG_TYPES.AI_PLAYER_REMOVED, (message) => {
      if (message.data && message.data.players) {
        setPlayers(message.data.players);
      }
    });

    return () => {
      unsubscribeGameService();
      unsubscribeJoined();
      unsubscribeLeft();
      unsubscribeStarted();
      unsubscribeGameState();
      unsubscribeKicked();
      unsubscribeAIAdded();
      unsubscribeAIRemoved();
    };
  }, [onMessage, MSG_TYPES, onGameStarted, onLeave, t.room.youWereKicked]);

  const handleStartGame = () => {
    if (players.length < 2) {
      alert(t.room.needTwoPlayers);
      return;
    }
    setGameStarting(true);
    startGame();
  };

  const handleLeaveRoom = () => {
    leaveRoom();
    gameService.reset();
    onLeave();
  };

  const handleCopyLink = () => {
    const link = inviteLink || `${window.location.origin}/?room=${roomCode}`;
    console.log('[ROOM] Copying invite link:', link);
    navigator.clipboard.writeText(link).then(() => {
      setLinkCopied(true);
      console.log('[ROOM] Link copied successfully');
      setTimeout(() => setLinkCopied(false), 2000); // Reset after 2 seconds
    }).catch((err) => {
      console.error('[ROOM] Failed to copy link:', err);
      alert(`Failed to copy link. Please copy manually: ${link}`);
    });
  };

  const handleKickPlayer = (playerId) => {
    if (window.confirm(t.room.kickConfirm)) {
      kickPlayer(playerId);
    }
  };

  const handleAddAI = () => {
    addAIPlayer();
  };

  const handleRemoveAI = (aiPlayerId) => {
    if (window.confirm(t.room.removeAIConfirm)) {
      removeAIPlayer(aiPlayerId);
    }
  };

  const aiPlayers = players.filter(p => p.isAI);
  const canStartGame = isCreator && players.length >= 2;

  return (
    <div className="screen room-screen">
      <div className="room-container">
        {reconnecting && (
          <div className="connection-banner reconnecting-banner">
            {t.connection.reconnecting.replace('{attempt}', reconnectAttempt)}
          </div>
        )}
        {!isConnected && !reconnecting && (
          <div className="connection-banner disconnected-banner">
            {t.connection.disconnected}
          </div>
        )}
        <div className="room-header">
          <h2 className="pixel-text">{t.room.title}</h2>
          <div className="room-code-display">
            <span className="room-code-label">{t.room.roomCodeLabel}</span>
            <span className="room-code">{roomCode}</span>
          </div>
          <div className="invite-link-display">
            <button
              className="pixel-button copy-link-button"
              onClick={handleCopyLink}
              title={inviteLink || `${window.location.origin}/?room=${roomCode}`}
            >
              {linkCopied ? t.room.linkCopied : t.room.copyLink}
            </button>
            <div className="invite-hint">{t.room.inviteHint}</div>
          </div>
        </div>

        <div className="room-content">
          <div className="players-section">
            <h3>{t.room.playersLabel} ({players.length})</h3>
            <div className="players-list">
              {players.map((player) => (
                <div
                  key={player.id}
                  className={`player-item ${player.isAI ? 'ai-player-item' : ''} ${player.isCreator ? 'player-creator' : ''} ${!player.isConnected ? 'player-disconnected' : ''}`}
                >
                  <span className="player-name">
                    {player.isAI && <span className="robot-icon">🤖</span>}
                    {player.name}
                    {player.isAI && player.personality && (
                      <span className="personality-hint" title={player.personality}>
                        {' '}- {player.personality}
                      </span>
                    )}
                    {!player.isConnected && <span className="disconnected-indicator"> {t.room.disconnected}</span>}
                  </span>
                  <div className="player-badges">
                    {player.isCreator && (
                      <span className="player-badge">{t.room.hostBadge}</span>
                    )}
                    {player.id === gameService.playerID && (
                      <span className="player-badge">{t.room.youBadge}</span>
                    )}
                    {isCreator && player.id !== gameService.playerID && !player.isAI && (
                      <button
                        className="kick-button"
                        onClick={() => handleKickPlayer(player.id)}
                        title={t.room.kickPlayerTitle}
                      >
                        ✕
                      </button>
                    )}
                    {isCreator && player.isAI && (
                      <button
                        className="remove-ai-button"
                        onClick={() => handleRemoveAI(player.id)}
                        title={t.room.removeAITitle}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {players.length < 2 && (
              <div className="room-info">
                {isCreator
                  ? t.room.waitingForPlayersHost
                  : t.room.waitingForPlayers}
              </div>
            )}

            {isCreator && (
              <div className="ai-controls">
                <h3>{t.room.aiControlsTitle}</h3>
                <button
                  className="add-ai-button"
                  onClick={handleAddAI}
                >
                  {t.room.addAIPlayer}
                </button>
                {aiPlayers.length === 0 && (
                  <div className="no-ai-message">{t.room.noAIPlayers}</div>
                )}
              </div>
            )}
          </div>

          <div className="room-instructions">
            <h3>{t.room.gameRules}</h3>
            <ul>
              {t.room.gameRulesItems.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="room-actions">
          {isCreator ? (
            <button
              className="pixel-button pixel-button-primary"
              onClick={handleStartGame}
              disabled={!canStartGame || gameStarting}
            >
              {gameStarting ? t.room.starting : t.room.startGame}
            </button>
          ) : (
            <div className="waiting-message">
              {t.room.waitingForHost}
            </div>
          )}
          <button
            className="pixel-button pixel-button-secondary"
            onClick={handleLeaveRoom}
            disabled={gameStarting}
          >
            {t.room.leaveRoom}
          </button>
        </div>

        {gameStarting && (
          <div className="game-starting-overlay">
            <div className="game-starting-message pixel-text">
              {t.room.gameStarting}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Room;
